import { useState } from 'react';
import { Button, Form, Stack } from 'react-bootstrap';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import {
  faArrowRotateRight,
  faCirclePlay,
  faInfoCircle,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import AIPatientConfirmationModal from 'src/products/AIIntegrated/pages/AIPatientConfirmationModal/AIPatientConfirmationModal';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import PatientInfoDrawer from 'src/components/custom_components/PatientInfoDrawer/PatientInfoDrawer';
import WarnNoConnectedCameraModal from 'src/components/custom_components/WarnNoConnectedCameraModal';

import { ApplicationInstance } from 'src/models/Application.model';
import { Camera } from 'src/models/Camera.model';
import { CameraMode } from 'src/models/CameraMode.model';
import { Gender } from 'src/models/Gender.model';
import { PatientInfoKeyMapOptain, Session } from 'src/models/Session.model';

import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useUiTranslation from 'src/hooks/useUiTranslation';

import CameraLanguageDropdown from '../custom_components/CameraLanguageDropdown';
import NewPatientCreation from '../NewPatientCreation/NewPatientCreation';
import SearchPatientInfoFormGroup from '../SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';

export interface SearchPatientFormValues {
  dob: string;
  lastName: string;
  firstName: string;
  id: string;
}

const NewPatientHeader = () => {
  const { currentUser } = useGetCurrentUser();

  const {
    cameraData,
    cameraMode,
    practiceSettings: { product, camera },
    userSettings: { userPermission },
    session,
  } = useRemoteBoundStore();

  const [formValues, setFormValues] = useState<Partial<Session>>(session);

  const [isShowConfirmModal, setIsShowConfirmModal] = useState<boolean>(false);
  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);

  const [showDrawer, setShowDrawer] = useState(false);

  // Search form for patient search functionality
  const searchMethods = useForm<SearchPatientFormValues>({
    defaultValues: {
      dob: '',
      lastName: '',
      firstName: '',
      id: '',
    },
  });

  const handleCloseConfirmModal = () => {
    setIsShowConfirmModal(false);
  };

  const onSubmit: SubmitHandler<Partial<Session>> = (data) => {
    const dob = moment(data.dob).format('YYYY-MM-DD');
    const age = moment().diff(data.dob, 'years');

    setFormValues({
      ...data,
      dob,
      age,
    });
    setIsShowConfirmModal(true);
  };

  const methods = useForm({
    defaultValues: {
      id: session.id,
      firstName: session.firstName,
      lastName: session.lastName,
      dob: session.dob,
      leftEyeDilatedTime: session.leftEyeDilatedTime,
      rightEyeDilatedTime: session.rightEyeDilatedTime,
      template: ApplicationInstance(product, camera).templates[0],
      gender: Gender.UNSPECIFIED,
      orderId: session.orderId,
    },
  });

  const updateOrderId = (orderId: string) => {
    setFormValues({ ...formValues, orderId });
  };

  const { tCommon, tPages } = useUiTranslation();
  return (
    <>
      <Stack gap={3} className="h-100">
        <div className="d-flex flex-column flex-md-row justify-content-md-between gap-3">
          <div className="flex-grow-1">
            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
              <div>
                <h3 style={{ whiteSpace: 'nowrap' }}>
                  {tPages('newExam.desc')}
                </h3>
              </div>
              <Button
                variant="primary-100"
                className="d-flex align-items-center"
                onClick={() => setIsNewPatientModalOpen(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                <span style={{ whiteSpace: 'nowrap' }}>
                  {tPages('newExam.createNewPatient')}
                </span>
              </Button>
            </div>
            <div className="text-primary d-flex flex-wrap gap-2 align-items-center mt-2">
              <FontAwesomeIcon icon={faCirclePlay} />
              {tPages('newExam.startNewExam')}
              <span>|</span>
              <FontAwesomeIcon icon={faInfoCircle} />
              {tCommon('orderFields.details')}
              <span>|</span>
              <FontAwesomeIcon icon={faArrowRotateRight} />
              {tPages('newExam.resume')}
            </div>
          </div>

          {camera === Camera.SMALL &&
            cameraMode === CameraMode.API &&
            cameraData && (
              <div className="flex-shrink-0">
                <CameraLanguageDropdown />
              </div>
            )}
        </div>
        <div
          className="flex-grow-1 d-flex flex-column"
          style={{ minHeight: 0 }}
        >
          <SearchPatientInfoFormGroup
            setShowConfirmModal={setIsShowConfirmModal}
            isCreatePatientEnabled={userPermission.create}
            setIsNewPatientModalOpen={setIsNewPatientModalOpen}
            setShowDrawer={setShowDrawer}
            setFormValues={setFormValues}
            searchMethods={searchMethods}
          />
        </div>

        {isNewPatientModalOpen && (
          <NewPatientCreation
            isOpen={isNewPatientModalOpen}
            onClose={() => setIsNewPatientModalOpen(false)}
            onMatchedPatient={(formValues: Partial<Session>) => {
              setFormValues(formValues);
              setIsNewPatientModalOpen(false);
              setIsShowConfirmModal(true);
            }}
            searchMethods={searchMethods}
          />
        )}

        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit)}>
            <AIPatientConfirmationModal
              formValues={formValues}
              isShowConfirmModal={isShowConfirmModal}
              handleCloseConfirmModal={handleCloseConfirmModal}
              InfoConfirmationKeyMap={PatientInfoKeyMapOptain}
              resetPatientForm={() => methods.reset()}
            />
          </Form>
        </FormProvider>
      </Stack>

      <PatientInfoDrawer
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        formValues={formValues}
        setShowConfirmModal={setIsShowConfirmModal}
        isCreateEnabled={userPermission.create}
        updateOrderId={updateOrderId}
      />

      {currentUser && (
        <WarnNoConnectedCameraModal
          isVisible={
            camera === Camera.SMALL &&
            cameraMode === CameraMode.API &&
            !cameraData
          }
        />
      )}
    </>
  );
};

export default NewPatientHeader;
